import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { LoginUser, SignupUser, ThirdPartyLogin, UserForgetPass, CodeVerify, ResetPassword } from "../../service/service";
import { setUserdata } from "../../store/slices/UserDataSlice";
import { takeAuthReturn } from "../api/engagement";
import { paths } from "../routing/paths";

/*
 * Auth flow per `Auth Flow.dc.html` (brief v2.2 §7.2.11) — one navy-
 * centered card refreshing the EXISTING auth stack (email/password +
 * Google SSO; Apple's button is present but its legacy handler was never
 * wired to the backend, so it links users to Google/email until the
 * backend adds Apple token verification — see the handoff notes).
 *
 * Modes: signin / signup / reset / reset-code / reset-sent. The backend's
 * reset flow is CODE-based (6-digit email code, then new password), so
 * after "Email me a reset link" the card asks for the code + new password
 * — same visual language as the reference's reset-sent screen.
 *
 * Return-to-exact-page: sign-in reads the return path saved by whoever
 * sent the user here (engagement buttons, header) and navigates back to
 * it; the player lives above the router, so a mid-class listener keeps
 * playing through the whole flow and lands back where they were.
 */

const inputClasses =
  "bg-[#F3F4F6] text-ic-navy font-sans text-[16px] leading-6 px-4 h-12 border border-transparent rounded-xl box-border w-full transition-all duration-150 ease-out focus:outline-none focus:bg-white focus:border-ic-blue focus:shadow-ic-focus-soft";
const primaryBtn =
  "bg-ic-blue text-ic-offwhite font-sans text-[16px] font-bold leading-6 h-12 border-0 rounded-3xl cursor-pointer w-full transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive focus-visible:outline-none focus-visible:shadow-ic-focus disabled:opacity-60 disabled:cursor-wait";
const ssoBtn =
  "inline-flex items-center justify-center gap-2.5 bg-transparent text-ic-navy font-sans text-[16px] font-semibold leading-6 h-12 border border-ic-borderGray rounded-3xl cursor-pointer w-full transition-all duration-150 ease-out hover:bg-black/[0.04] hover:border-[#B4B4B4] focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft";

function Label({ children }) {
  return <span className="text-[14px] font-bold leading-5 text-ic-navy">{children}</span>;
}

const GoogleMark = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.93-2.91l-3.87-3c-1.07.72-2.44 1.14-4.06 1.14-3.12 0-5.77-2.11-6.71-4.95H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.29 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.29a12 12 0 0 0 0 10.76l4-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.6 4.58 1.79l3.44-3.44A12 12 0 0 0 1.29 6.62l4 3.1C6.23 6.88 8.88 4.77 12 4.77z" />
  </svg>
);

export default function AuthPage({ initialMode = "signin" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mode, setMode] = useState(initialMode); // signin | signup | reset | reset-code | reset-sent
  const [hidden, setHidden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "", confirm: "" });
  const [resetUser, setResetUser] = useState(null); // {id, email} from the forgot step

  const isSignIn = mode === "signin";
  const isSignUp = mode === "signup";
  const isAuth = isSignIn || isSignUp;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const go = (m) => {
    setMode(m);
    setError("");
  };

  const finishAuth = (result) => {
    localStorage.setItem("accessToken", result.token);
    localStorage.setItem("loginData", JSON.stringify(result.data));
    dispatch(setUserdata({ userdata: result.data }));
    // Back to the exact page the user left — mid-class playback never
    // stopped (the player lives above the router).
    navigate(takeAuthReturn() || paths.home(), { replace: true });
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = isSignIn
        ? await LoginUser({ email: form.email, password: form.password })
        : await SignupUser({ name: form.name, email: form.email, password: form.password });
      if (result?.status && result.token) finishAuth(result);
      else setError(result?.message || "Something went wrong — try again.");
    } catch (_) {
      setError("Something went wrong — try again.");
    }
    setBusy(false);
  };

  const handleGoogle = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setBusy(true);
      setError("");
      try {
        const res = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${codeResponse.access_token}`,
          { headers: { Authorization: `Bearer ${codeResponse.access_token}`, Accept: "application/json" } }
        );
        const result = await ThirdPartyLogin({ ...res.data, type: "google" });
        if (result?.status && result.token) finishAuth(result);
        else setError(result?.message || "Google sign-in didn't complete — try again.");
      } catch (_) {
        setError("Google sign-in didn't complete — try again.");
      }
      setBusy(false);
    },
    onError: () => setError("Google sign-in didn't complete — try again."),
  });

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await UserForgetPass({ email: form.email, is_admin: false });
      if (result?.status) {
        setResetUser({ id: result?.data?.id, email: form.email });
        go("reset-sent");
      } else {
        setError(result?.message || "We couldn't find an account with that email.");
      }
    } catch (_) {
      setError("Something went wrong — try again.");
    }
    setBusy(false);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const verified = await CodeVerify({ code: form.code, user_id: resetUser?.id });
      if (!verified?.status) {
        setError(verified?.message || "That code didn't match — check the email and try again.");
        setBusy(false);
        return;
      }
      const result = await ResetPassword({
        password: form.password,
        confirm_password: form.confirm || form.password,
        is_admin: false,
        id: resetUser?.id,
      });
      if (result?.status) {
        go("signin");
        setForm((f) => ({ ...f, password: "", code: "" }));
      } else {
        setError(result?.message || "Something went wrong — try again.");
      }
    } catch (_) {
      setError("Something went wrong — try again.");
    }
    setBusy(false);
  };

  const headline = isSignIn
    ? "Welcome back"
    : isSignUp
    ? "Start learning today"
    : mode === "reset"
    ? "Reset your password"
    : mode === "reset-code"
    ? "Choose a new password"
    : "Check your email";
  const subline = isSignIn
    ? "15,000+ audio and video classes — pick up any class where you left off, on any device."
    : isSignUp
    ? "A free account remembers your place in every class — on any device."
    : mode === "reset"
    ? "It happens to everyone. We'll have you back in a minute."
    : mode === "reset-code"
    ? "Type the 6-digit code from the email and pick a new password."
    : "The email has a 6-digit code — it expires quickly, so use it soon.";

  const tabBtn = (active) =>
    `flex-1 h-11 border-0 rounded-[20px] font-sans text-[16px] font-bold cursor-pointer transition-all duration-150 ease-out focus-visible:outline-none focus-visible:shadow-ic-focus ${
      active ? "bg-ic-navy text-white shadow-[0px_1px_3px_rgba(0,0,0,0.2)]" : "bg-transparent text-ic-slate"
    }`;

  return (
    <div className="ic-scope min-h-screen bg-ic-navy flex flex-col font-sans">
      <header className="p-5">
        <div className="max-w-[1280px] mx-auto">
          <Link to={paths.home()} aria-label="Inside Chassidus home" className="inline-flex items-center gap-2.5 no-underline hover:no-underline">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-ic-blue inline-flex items-center justify-center text-white text-[16px] font-extrabold">IC</span>
            <span className="text-ic-offwhite text-[18px] font-bold leading-7 whitespace-nowrap">Inside Chassidus</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-5 pb-12" style={{ paddingTop: "clamp(16px, 5vh, 56px)" }}>
        <div className="w-full max-w-[440px] flex flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="m-0 font-extrabold text-white tracking-[-0.4px]" style={{ fontSize: "clamp(24px, 5vw, 30px)", lineHeight: 1.25 }}>
              {headline}
            </h1>
            <p className="m-0 text-[16px] leading-6 text-ic-lightGray">{subline}</p>
          </div>

          <div className="bg-white rounded-[32px] shadow-[0px_4px_12px_rgba(0,0,0,0.4)] flex flex-col gap-5" style={{ padding: "clamp(24px, 6vw, 32px)" }}>
            {isAuth && (
              <>
                <div role="tablist" aria-label="Sign in or create account" className="flex gap-1 bg-[#F3F4F6] rounded-3xl p-1">
                  <button type="button" role="tab" aria-selected={isSignIn} onClick={() => go("signin")} className={tabBtn(isSignIn)}>
                    Sign in
                  </button>
                  <button type="button" role="tab" aria-selected={isSignUp} onClick={() => go("signup")} className={tabBtn(isSignUp)}>
                    Create account
                  </button>
                </div>

                {/* SSO first */}
                <div className="flex flex-col gap-3">
                  <button type="button" onClick={() => handleGoogle()} disabled={busy} className={ssoBtn}>
                    <GoogleMark />
                    {isSignUp ? "Sign up with Google" : "Continue with Google"}
                  </button>
                </div>

                <div className="flex items-center gap-3" aria-hidden="true">
                  <div className="flex-1 h-px bg-ic-lightGray" />
                  <span className="text-[14px] leading-5 text-ic-slate">or with email</span>
                  <div className="flex-1 h-px bg-ic-lightGray" />
                </div>

                {error && (
                  <div role="alert" className="flex items-start gap-2.5 bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl px-4 py-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 8v4" />
                      <path d="M12 16h.01" />
                    </svg>
                    <span className="text-[15px] leading-[22px] text-[#991B1B]">
                      {error}{" "}
                      {isSignIn && (
                        <button type="button" onClick={() => go("signup")} className="bg-transparent border-0 p-0 font-sans text-[15px] text-[#991B1B] underline cursor-pointer">
                          create a free account
                        </button>
                      )}
                    </span>
                  </div>
                )}

                <form onSubmit={submitAuth} className="flex flex-col gap-4">
                  {isSignUp && (
                    <label className="flex flex-col gap-1.5">
                      <Label>
                        Name <span className="font-normal text-ic-slate">(optional)</span>
                      </Label>
                      <input type="text" name="name" autoComplete="name" placeholder="Your name" value={form.name} onChange={set("name")} className={inputClasses} />
                    </label>
                  )}
                  <label className="flex flex-col gap-1.5">
                    <Label>Email</Label>
                    <input
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={set("email")}
                      className={inputClasses}
                      style={error ? { borderColor: "#DC2626" } : undefined}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <Label>Password</Label>
                      {isSignIn && (
                        <button type="button" onClick={() => go("reset")} className="bg-transparent border-0 p-0 font-sans text-[14px] leading-5 text-ic-blue font-semibold cursor-pointer hover:underline">
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={hidden ? "password" : "text"}
                        name="password"
                        required
                        autoComplete={isSignIn ? "current-password" : "new-password"}
                        placeholder={isSignIn ? "Your password" : "Choose a password"}
                        value={form.password}
                        onChange={set("password")}
                        className={inputClasses}
                        style={{ paddingRight: 52 }}
                      />
                      <button
                        type="button"
                        aria-label={hidden ? "Show password" : "Hide password"}
                        onClick={() => setHidden((h) => !h)}
                        className="absolute right-0.5 top-0.5 w-11 h-11 bg-transparent border-0 rounded-xl text-ic-slate inline-flex items-center justify-center cursor-pointer transition-all duration-150 ease-out hover:text-ic-navy focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
                      >
                        {hidden ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17.94 17.94A10.5 10.5 0 0 1 12 19c-6.5 0-10-7-10-7a19 19 0 0 1 4.06-4.94" />
                            <path d="M9.9 4.24A10 10 0 0 1 12 5c6.5 0 10 7 10 7a19 19 0 0 1-2.16 3.19" />
                            <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                            <path d="M2 2l20 20" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </label>
                  {isSignUp && <span className="text-[14px] leading-5 text-ic-slate -mt-2">At least 8 characters. That's the only rule.</span>}
                  <button type="submit" disabled={busy} className={primaryBtn}>
                    {busy ? "One moment…" : isSignIn ? "Sign in" : "Create free account"}
                  </button>
                </form>
              </>
            )}

            {mode === "reset" && (
              <form onSubmit={submitReset} className="flex flex-col gap-4">
                <p className="m-0 text-[16px] leading-6 text-ic-body">
                  Type the email you signed up with and we'll send you a code to choose a new password. No other questions asked.
                </p>
                {error && (
                  <div role="alert" className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl px-4 py-3 text-[15px] leading-[22px] text-[#991B1B]">{error}</div>
                )}
                <label className="flex flex-col gap-1.5">
                  <Label>Email</Label>
                  <input type="email" name="email" required autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set("email")} className={inputClasses} />
                </label>
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? "One moment…" : "Email me a reset code"}
                </button>
                <button type="button" onClick={() => go("signin")} className="self-center bg-transparent border-0 p-0 font-sans text-[15px] text-ic-blue font-semibold cursor-pointer min-h-[44px] hover:underline">
                  ← Back to sign in
                </button>
              </form>
            )}

            {mode === "reset-sent" && (
              <div className="flex flex-col gap-4 items-center text-center">
                <span className="w-16 h-16 rounded-full bg-[rgba(36,154,26,0.1)] text-ic-success inline-flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 12.5L9 17.5L20 6.5" />
                  </svg>
                </span>
                <p className="m-0 text-[18px] font-semibold leading-7 text-ic-navy">
                  The code is on its way to
                  <br />
                  {resetUser?.email || form.email}
                </p>
                <p className="m-0 text-[16px] leading-6 text-ic-body">
                  Open the email and use the 6-digit code to choose a new password. It can take a minute to arrive — check your spam folder if you don't see it.
                </p>
                <button type="button" onClick={() => go("reset-code")} className={primaryBtn}>
                  I have the code
                </button>
                <button type="button" onClick={submitReset} disabled={busy} className="bg-transparent text-ic-blue font-sans text-[16px] font-bold leading-6 h-12 px-6 border border-ic-blue rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-[rgba(24,108,206,0.08)] focus-visible:outline-none focus-visible:shadow-ic-focus-soft">
                  Didn't get it? Send it again
                </button>
                <button type="button" onClick={() => go("signin")} className="bg-transparent border-0 p-0 font-sans text-[15px] text-ic-blue font-semibold cursor-pointer min-h-[44px] hover:underline">
                  ← Back to sign in
                </button>
              </div>
            )}

            {mode === "reset-code" && (
              <form onSubmit={submitCode} className="flex flex-col gap-4">
                {error && (
                  <div role="alert" className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl px-4 py-3 text-[15px] leading-[22px] text-[#991B1B]">{error}</div>
                )}
                <label className="flex flex-col gap-1.5">
                  <Label>6-digit code</Label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required placeholder="123456" value={form.code} onChange={set("code")} className={inputClasses} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <Label>New password</Label>
                  <input type={hidden ? "password" : "text"} required autoComplete="new-password" placeholder="Choose a password" value={form.password} onChange={set("password")} className={inputClasses} />
                </label>
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? "One moment…" : "Set new password"}
                </button>
                <button type="button" onClick={() => go("reset")} className="self-center bg-transparent border-0 p-0 font-sans text-[15px] text-ic-blue font-semibold cursor-pointer min-h-[44px] hover:underline">
                  ← Start over
                </button>
              </form>
            )}
          </div>

          {isSignIn && (
            <p className="m-0 text-center text-[15px] leading-[22px] text-ic-lightGray">
              New here?{" "}
              <button type="button" onClick={() => go("signup")} className="bg-transparent border-0 p-0 font-sans text-[15px] text-[#7EB3F0] font-semibold cursor-pointer hover:underline">
                Create a free account
              </button>
            </p>
          )}
          {isSignUp && (
            <p className="m-0 text-center text-[15px] leading-[22px] text-ic-lightGray">
              By creating an account you agree to the{" "}
              <Link to={paths.privacy()} className="text-[#7EB3F0]">
                privacy policy
              </Link>
              .
            </p>
          )}
          {isAuth && (
            <p className="m-0 text-center text-[15px] leading-[22px] text-[#9CA3AF]">
              You can listen to every class without an account.
              <br />
              <Link to={takeAuthReturnPreview() || paths.home()} className="text-[#7EB3F0]">
                Continue as a guest
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

/** Peek (without consuming) at the stored return path for the guest link. */
function takeAuthReturnPreview() {
  try {
    return sessionStorage.getItem("ic-auth-return");
  } catch (_) {
    return null;
  }
}
